/**
 * Dashboards store — Context + provider holding the tenant's dashboards
 * list, with per-mutation actions that drive both the user-facing editor
 * (B0.42 refactor) and the copilot's authoring tools (B0.43).
 *
 * Mirrors the PlantProvider / ComputedTagsProvider pattern. Persists to
 * localStorage `divineiot:dashboards` so user edits survive reload.
 * Phase 1 swaps the persistence layer for `/v1/iot/dashboards` CRUD;
 * the React API stays identical.
 *
 * The "structural damage" guard: every mutation method checks
 * `isEditable()` first and throws on pack-shipped dashboards. The
 * caller (UI or copilot dispatcher) is expected to catch + surface as
 * a user-visible error.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ALL_FIXTURE_DASHBOARDS } from "./fixtures";
import { BoardWriter, mergeServerBoards } from "./board-writer";
import { withLayouts, type LayoutWrite } from "./layout-save";
import {
  useDashboardsQuery,
  toDashboardConfig,
  backendErrorMessage,
  writeErrorMessage,
  isLiveApi,
  dashboards as dashboardsApi,
} from "@/lib/api";
import { useToast } from "@/lib/ui/toast";
import type {
  DashboardConfig,
  DataSource,
  WidgetInstance,
} from "./types";

const STORAGE_KEY = "divineiot:dashboards";

/** The toast when an autosave fails; the server's own sentence, or the status, follows it (writeErrorMessage). */
const AUTOSAVE_FAILED = "Couldn't save your changes to this board. They stay on screen, and the next change tries again.";

export class DashboardEditError extends Error {
  readonly code: "read_only" | "not_found";
  constructor(code: "read_only" | "not_found", message: string) {
    super(message);
    this.code = code;
    this.name = "DashboardEditError";
  }
}

export function isEditable(d: Pick<DashboardConfig, "source">): boolean {
  return d.source !== "pack";
}

export interface DashboardsState {
  dashboards: DashboardConfig[];
  /** The first read of the boards is on its way: a board not in the list may still be in it. */
  loading: boolean;
  byId: (id: string) => DashboardConfig | undefined;

  /** Create a new user-owned dashboard. Returns the new id. */
  create: (
    seed: Omit<DashboardConfig, "id" | "source" | "owner" | "updatedAt"> & {
      id?: string;
      source?: "my" | "shared";
      owner?: string;
    },
  ) => string;

  /** Clone an existing dashboard (pack or user). Always lands as user-owned. */
  clone: (sourceId: string, newName?: string) => string;

  /** Replace the whole dashboard (used on Save in the editor). */
  update: (id: string, patch: Partial<DashboardConfig>) => void;

  /**
   * Write this dashboard to the server now and wait for the answer (live mode; stub mode has nothing to wait for).
   * Rejects when the write fails, leaving the edit in place so a later write retries it.
   */
  save: (id: string) => Promise<void>;

  /** Delete a dashboard (user-owned only). */
  remove: (id: string) => void;

  /** Move or resize the widgets a drop moved, all in one update (layout-save.ts withLayouts). */
  updateLayouts: (dashboardId: string, writes: readonly LayoutWrite[]) => void;

  /** Mutate one widget's properties (title / subtitle / config). */
  updateWidget: (
    dashboardId: string,
    widgetId: string,
    patch: Partial<WidgetInstance>,
  ) => void;

  /** Mutate one widget's data source. */
  setWidgetDataSource: (
    dashboardId: string,
    widgetId: string,
    dataSource: DataSource,
  ) => void;

  /** Add a widget to a dashboard. */
  addWidget: (dashboardId: string, widget: WidgetInstance) => void;

  /** Remove a widget. */
  removeWidget: (dashboardId: string, widgetId: string) => void;

  /** Reset all user edits — restore the fixture seed. */
  reset: () => void;

  /** Mutation counter — drives memoised consumers. */
  version: number;
}

const DashboardsContext = createContext<DashboardsState | null>(null);

export function useDashboards(): DashboardsState {
  const ctx = useContext(DashboardsContext);
  if (!ctx) {
    throw new Error("useDashboards must be used inside <DashboardsProvider>");
  }
  return ctx;
}

export function useOptionalDashboards(): DashboardsState | null {
  return useContext(DashboardsContext);
}

function readPersisted(): DashboardConfig[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed as DashboardConfig[];
  } catch {
    return null;
  }
}

function writePersisted(dashboards: DashboardConfig[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(dashboards));
  } catch {
    /* quota / disabled — swallow */
  }
}

/**
 * Mint a new dashboard id. In live mode the id is the durable backend's PK
 * (uuid column) — the SPA owns it (create/clone are synchronous) and the
 * server upserts on it (mirrors reports). Stub mode keeps the legacy `dash-…`
 * ids (localStorage only).
 */
function newDashboardId(): string {
  if (isLiveApi && typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `dash-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
}

export function DashboardsProvider({ children }: { children: ReactNode }) {
  // Live mode (real multi-tenant backend): start EMPTY and let the org-
  // scoped query populate — never seed from client fixtures or the global
  // localStorage (both leak the foundry demo into every org). Fixtures +
  // localStorage are the stub/dev-loop path only.
  const [dashboards, setDashboards] = useState<DashboardConfig[]>(
    () => (isLiveApi ? [] : (readPersisted() ?? ALL_FIXTURE_DASHBOARDS)),
  );
  const [version, setVersion] = useState(0);

  // Persist on every change — stub mode only; in live mode the server +
  // React Query are the source of truth (a global localStorage key would
  // leak one org's dashboards into the next).
  useEffect(() => {
    if (!isLiveApi) writePersisted(dashboards);
  }, [dashboards]);

  const bumpVersion = useCallback(() => setVersion((v) => v + 1), []);

  // Server persistence (live mode only) — mirrors the reports store. Layout drags + widget edits fire rapidly, so the
  // writer coalesces to one PATCH per dashboard ~700ms after the last change (board-writer.ts). The whole config blob
  // is sent each time (the server upserts it). create/clone POST immediately (one-shot, client UUID); remove cancels
  // any pending save then DELETEs (so an in-flight upsert can't resurrect a just-deleted dashboard). The writer also
  // keeps the ["dashboards"] cache in step with all three, because the sync below reads the list from it.
  const queryClient = useQueryClient();
  const toast = useToast();
  const [writer] = useState(
    () =>
      new BoardWriter({
        queryClient,
        update: (id, body) => dashboardsApi.update(id, body),
        create: (body) => dashboardsApi.create(body),
        remove: (id) => dashboardsApi.remove(id),
        onAutosaveError: (err) => toast.push("error", writeErrorMessage(err, AUTOSAVE_FAILED)),
      }),
  );
  const schedulePersist = useCallback(
    (dash: DashboardConfig) => {
      if (isLiveApi) writer.schedule(dash);
    },
    [writer],
  );

  // ── live-API wiring (org-scoped) ──────────────────────────────────
  // On live data, show ONLY the active org's server dashboards. We no
  // longer prepend client-side pack fixtures — that injected the foundry
  // demo into every org (including brand-new empty ones), which is exactly
  // the cross-tenant leak we're closing. The seeded demo lives server-side
  // in its own org (default), so it arrives through this same path.
  const query = useDashboardsQuery();
  const erroredRef = useRef(false);
  const [listed, setListed] = useState(false);

  useEffect(() => {
    if (query.data) {
      // Don't let a background refetch (e.g. window-focus) clobber a local edit
      // the server has not answered yet — that would snap a drag back. The
      // writer holds each board from its edit until the PATCH answers (and
      // after a failed one, which is the only copy of the edit), ignores a read
      // older than the board's last answered write, and keeps deleted boards
      // out; every other board takes the server's copy. The saved board lands
      // in this same cache, so the next sync agrees with the screen.
      const guard = writer.syncGuard();
      setDashboards((local) => mergeServerBoards(local, query.data.items.map(toDashboardConfig), guard));
      setListed(true);
      bumpVersion();
    }
  }, [query.data, bumpVersion, writer]);

  useEffect(() => {
    if (query.error && !erroredRef.current) {
      erroredRef.current = true;
      toast.push("error", backendErrorMessage(query.error));
    }
  }, [query.error, toast]);
  // ──────────────────────────────────────────────────────────────────

  const byId = useCallback(
    (id: string) => dashboards.find((d) => d.id === id),
    [dashboards],
  );

  const findOrThrow = useCallback(
    (id: string): DashboardConfig => {
      const d = dashboards.find((x) => x.id === id);
      if (!d) {
        throw new DashboardEditError("not_found", `Dashboard "${id}" not found.`);
      }
      return d;
    },
    [dashboards],
  );

  const ensureEditable = useCallback(
    (id: string): DashboardConfig => {
      const d = findOrThrow(id);
      if (!isEditable(d)) {
        throw new DashboardEditError(
          "read_only",
          `"${d.name}" is a pack-shipped dashboard — clone it before editing.`,
        );
      }
      return d;
    },
    [findOrThrow],
  );

  const create = useCallback<DashboardsState["create"]>((seed) => {
    const id = seed.id ?? newDashboardId();
    const now = new Date().toISOString();
    const next: DashboardConfig = {
      id,
      name: seed.name,
      description: seed.description,
      cols: seed.cols,
      rowHeight: seed.rowHeight,
      widgets: seed.widgets ?? [],
      source: seed.source ?? "my",
      pack: seed.pack,
      owner: seed.owner ?? "You",
      updatedAt: now,
    };
    setDashboards((prev) => [next, ...prev]);
    bumpVersion();
    // Live mode: persist the whole config blob immediately (the mapper hydrates
    // it back on load; the envelope id/name/updated_at win). Fire-and-forget —
    // a failed write degrades to local-only, it doesn't block authoring. The
    // writer also puts the board in the ["dashboards"] cache, which the sync reads.
    if (isLiveApi) writer.add(next);
    return id;
  }, [bumpVersion, writer]);

  const clone = useCallback<DashboardsState["clone"]>(
    (sourceId, newName) => {
      const src = findOrThrow(sourceId);
      const id = newDashboardId();
      const now = new Date().toISOString();
      const cloned: DashboardConfig = {
        ...src,
        id,
        name: newName ?? `${src.name} (copy)`,
        source: "my",
        pack: undefined,
        owner: "You",
        updatedAt: now,
        // Deep clone widgets so edits don't leak back into the source.
        widgets: src.widgets.map((w) => ({
          ...w,
          id: `${w.id}-clone-${Math.floor(Math.random() * 1000)}`,
          layout: { ...w.layout },
          dataSource: w.dataSource ? { ...w.dataSource } : undefined,
          config: w.config ? { ...w.config } : undefined,
        })),
      };
      setDashboards((prev) => [cloned, ...prev]);
      bumpVersion();
      if (isLiveApi) writer.add(cloned);
      return id;
    },
    [findOrThrow, bumpVersion, writer],
  );

  const update = useCallback<DashboardsState["update"]>(
    (id, patch) => {
      const target = ensureEditable(id);
      const now = new Date().toISOString();
      const updated: DashboardConfig = { ...target, ...patch, id, updatedAt: now };
      setDashboards((prev) => prev.map((d) => (d.id === id ? updated : d)));
      bumpVersion();
      schedulePersist(updated);
    },
    [ensureEditable, bumpVersion, schedulePersist],
  );

  const save = useCallback<DashboardsState["save"]>(
    async (id) => {
      const target = ensureEditable(id);
      if (!isLiveApi) return;
      await writer.save(target);
    },
    [ensureEditable, writer],
  );

  const remove = useCallback<DashboardsState["remove"]>(
    (id) => {
      ensureEditable(id);
      setDashboards((prev) => prev.filter((d) => d.id !== id));
      bumpVersion();
      if (isLiveApi) writer.forget(id);
    },
    [ensureEditable, bumpVersion, writer],
  );

  const mutateWidget = useCallback(
    (
      dashboardId: string,
      widgetId: string,
      transform: (w: WidgetInstance) => WidgetInstance,
    ) => {
      const target = ensureEditable(dashboardId);
      const now = new Date().toISOString();
      const updated: DashboardConfig = {
        ...target,
        widgets: target.widgets.map((w) => (w.id === widgetId ? transform(w) : w)),
        updatedAt: now,
      };
      setDashboards((prev) => prev.map((d) => (d.id === dashboardId ? updated : d)));
      bumpVersion();
      schedulePersist(updated);
    },
    [ensureEditable, bumpVersion, schedulePersist],
  );

  // Every widget a drop moved in one update: one mutateWidget each built each from the same board, and only the last
  // stuck.
  const updateLayouts = useCallback<DashboardsState["updateLayouts"]>(
    (dashboardId, writes) => {
      if (writes.length === 0) return;
      const target = ensureEditable(dashboardId);
      const updated: DashboardConfig = {
        ...target,
        widgets: withLayouts(target.widgets, writes),
        updatedAt: new Date().toISOString(),
      };
      setDashboards((prev) => prev.map((d) => (d.id === dashboardId ? updated : d)));
      bumpVersion();
      schedulePersist(updated);
    },
    [ensureEditable, bumpVersion, schedulePersist],
  );

  const updateWidget = useCallback<DashboardsState["updateWidget"]>(
    (dashboardId, widgetId, patch) => {
      mutateWidget(dashboardId, widgetId, (w) => ({ ...w, ...patch, id: w.id }));
    },
    [mutateWidget],
  );

  const setWidgetDataSource = useCallback<DashboardsState["setWidgetDataSource"]>(
    (dashboardId, widgetId, dataSource) => {
      mutateWidget(dashboardId, widgetId, (w) => ({ ...w, dataSource }));
    },
    [mutateWidget],
  );

  const addWidget = useCallback<DashboardsState["addWidget"]>(
    (dashboardId, widget) => {
      const target = ensureEditable(dashboardId);
      const now = new Date().toISOString();
      const updated: DashboardConfig = {
        ...target,
        widgets: [...target.widgets, widget],
        updatedAt: now,
      };
      setDashboards((prev) => prev.map((d) => (d.id === dashboardId ? updated : d)));
      bumpVersion();
      schedulePersist(updated);
    },
    [ensureEditable, bumpVersion, schedulePersist],
  );

  const removeWidget = useCallback<DashboardsState["removeWidget"]>(
    (dashboardId, widgetId) => {
      const target = ensureEditable(dashboardId);
      const now = new Date().toISOString();
      const updated: DashboardConfig = {
        ...target,
        widgets: target.widgets.filter((w) => w.id !== widgetId),
        updatedAt: now,
      };
      setDashboards((prev) => prev.map((d) => (d.id === dashboardId ? updated : d)));
      bumpVersion();
      schedulePersist(updated);
    },
    [ensureEditable, bumpVersion, schedulePersist],
  );

  const reset = useCallback(() => {
    setDashboards(isLiveApi ? [] : ALL_FIXTURE_DASHBOARDS);
    bumpVersion();
  }, [bumpVersion]);

  // Through the one commit where the read has its data and the list is not set from it yet.
  const loading = query.isLoading || (query.isSuccess && !listed);
  const value = useMemo<DashboardsState>(
    () => ({
      dashboards,
      loading,
      byId,
      create,
      clone,
      update,
      save,
      remove,
      updateLayouts,
      updateWidget,
      setWidgetDataSource,
      addWidget,
      removeWidget,
      reset,
      version,
    }),
    [
      dashboards,
      loading,
      byId,
      create,
      clone,
      update,
      save,
      remove,
      updateLayouts,
      updateWidget,
      setWidgetDataSource,
      addWidget,
      removeWidget,
      reset,
      version,
    ],
  );

  return (
    <DashboardsContext.Provider value={value}>
      {children}
    </DashboardsContext.Provider>
  );
}
